import React, { useEffect, useState } from "react";
import { Text, Flex, Icon, Box, type IconProps } from "@chakra-ui/react";
import { stagger, useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { AddBoxIcon, PlusIcon, TraitIcon } from "ui/icons";
import { Row } from "./row";
import { Container } from "./container";
import { FilterOption } from "./filter-option";
import { FilterButton } from "./filter-button";

const fadeIn = { opacity: 1 };
const fadeOut = { opacity: 0 };
const resetTiming = { duration: 0.05 };

function useTraitsAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                [".traits-fade-in", fadeIn, { at: "+0.8", delay: stagger(0.35) }],
              ]
            : [
                // Reset
                [".traits-fade-in", fadeOut, resetTiming],
              ],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function TraitsAnimation() {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useTraitsAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.8 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex
      ref={scope}
      align="center"
      bgGradient="linear(140deg, green.50 -40%, mint 160%)"
      boxSize="100%"
      pos="relative"
      pl={{ base: 2.5, lg: 4, xl: 7 }}
      py={{ base: 6, lg: 7 }}
    >
      <Flex
        align="start"
        bg="base.lightBackground"
        borderLeftRadius="lg"
        boxShadow="base"
        direction="column"
        flex={1}
        justify="start"
        maxH="340px"
        mx="auto"
        pos="relative"
        pt={{ base: 5, lg: 7 }}
        pl={{ base: 2.5, lg: 5, xl: 7 }}
        pb={8}
      >
        <Flex direction="column" gap={4} w="100%">
          <Text fontSize="sm" fontWeight="medium">
            Define your audience
          </Text>

          <Container>
            <Row>
              <Text as="span" color="product.gray.600">
                Has
              </Text>
              <TraitIcon boxSize={4} color="product.warning.400" />
              <Text as="span" fontWeight="medium">
                Trait calculation
              </Text>
            </Row>

            <Row>
              <FilterOption>Sum</FilterOption>

              <FilterOption className="traits-fade-in" opacity={0}>
                Price
              </FilterOption>

              <Text as="span" className="traits-fade-in" color="product.gray.600" whiteSpace="nowrap" opacity={0}>
                of events in
              </Text>

              <FilterOption className="traits-fade-in" opacity={0}>
                <EventIcon boxSize={{ base: 2.5, lg: 3, xl: 4 }} />
                Order Completed
              </FilterOption>

              <FilterOption className="traits-fade-in" label="greater than" opacity={0}>
                50th percentile
              </FilterOption>
            </Row>

            <Row>
              <AddBoxIcon boxSize={{ base: 2.5, lg: 3 }} color="text.secondary" ml={2} />
              <Text fontWeight="semibold" fontSize={{ base: "8px", lg: "10px" }}>
                Where...
              </Text>
            </Row>
          </Container>

          <FilterButton textTransform="uppercase">and</FilterButton>

          <FilterButton fontSize={{ base: "9px", lg: "10px", xl: "12px" }} px={{ base: 2, lg: 3 }}>
            <PlusIcon boxSize={{ base: "10px", lg: "12px", xl: "14px" }} />
            Add filter
          </FilterButton>
        </Flex>
      </Flex>
    </Flex>
  );
}

function EventIcon(props: IconProps) {
  return (
    <Icon width="17px" height="17px" fill="none" viewBox="0 0 17 17" {...props}>
      <rect width="15.992" height="15.992" x=".721" y=".322" fill="#3DBFD9" rx="4.798" />
      <rect width="15.992" height="15.992" x=".721" y=".322" fill="url(#event-icon-a)" rx="4.798" />
      <Box
        as="path"
        fill="#fff"
        d="M8.718 4.72a3.609 3.609 0 0 0-3.599 3.598 3.609 3.609 0 0 0 3.599 3.599 3.609 3.609 0 0 0 3.598-3.599A3.609 3.609 0 0 0 8.718 4.72Zm0 6.477a2.882 2.882 0 0 1-2.879-2.879A2.882 2.882 0 0 1 8.718 5.44a2.882 2.882 0 0 1 2.878 2.878 2.882 2.882 0 0 1-2.878 2.879Zm.18-4.678h-.54v2.16l1.87 1.15.289-.467-1.62-.972V6.52Z"
      />
      <Box as="defs">
        <linearGradient id="event-icon-a" x1="8.718" x2="8.718" y1=".322" y2="16.315" gradientUnits="userSpaceOnUse">
          <stop stopOpacity="0" />
          <stop offset="1" stopOpacity=".1" />
        </linearGradient>
      </Box>
    </Icon>
  );
}
